'use client';

import { useForm } from 'react-hook-form';
import z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  sendOtp,
  signIn,
  signUp,
  verifyOtp,
  volunteerOtp,
  verifyMfaLoginOtp,
  resendMfaLoginOtp
} from '@/services/webApi';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { cleanPhoneNumber } from '@/services/phone';
import {
  logInOTPBodySchema,
  signUpRequestSchema,
  signInRequestSchema,
  emailSchema
} from '@goodneighbor/api/schemas/auth';
import { signUpVerifyRequestSchema } from '@goodneighbor/api/routes/auth/v2/signUp/signUp.schema';
import useAccountQuery from '@/hooks/useAccountQuery';
import { getJWT } from '@/services/jwt';

type PhoneFormValues = z.infer<typeof logInOTPBodySchema>;
type SignUpVerifyRequestValues = z.infer<typeof signUpVerifyRequestSchema>;
type SignInRequestValues = z.infer<typeof signInRequestSchema>;

const otpSchema = z.object({ otp: z.string().min(6).max(6) });
type OtpFormValues = z.infer<typeof otpSchema>;

export type MfaLoginState = {
  mfaToken: string;
  mfaMethod: 'EMAIL' | 'SMS';
  maskedContact: string;
};

export default function useAuthHooks() {
  const account = useAccountQuery({
    enabled: !!getJWT()
  });
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [mfaLoginState, setMfaLoginState] = useState<MfaLoginState | null>(
    null
  );
  const router = useRouter();
  const queryClient = useQueryClient();
  const pathname = usePathname();

  const redirectAfterLogin = async () => {
    const accountResponse = await account.refetch();
    const accountData = accountResponse.data?.data;
    const isInSignInRoute =
      pathname.includes('/volunteer') && !pathname.includes('/volunteer/');
    if (accountData && isInSignInRoute) {
      router.push('/events');
    }
  };

  const sendOtpMutation = useMutation({
    mutationFn: sendOtp,
    onSuccess: () => {
      setStep('otp');
    }
  });

  const verifyOtpMutation = useMutation({
    mutationFn: verifyOtp,
    onSuccess: () => {
      account.refetch();
    }
  });

  const signInMutation = useMutation({
    mutationFn: signIn,
    onSuccess: async (response) => {
      if (response && !('data' in response)) {
        setMfaLoginState({
          mfaToken: response.mfaToken,
          mfaMethod: response.mfaMethod as 'EMAIL' | 'SMS',
          maskedContact: response.maskedContact
        });
        return;
      }
      await redirectAfterLogin();
    }
  });

  const verifyMfaLoginMutation = useMutation({
    mutationFn: ({ otp, mfaToken }: { otp: string; mfaToken: string }) =>
      verifyMfaLoginOtp({ otp }, mfaToken),
    onSuccess: async () => {
      setMfaLoginState(null);
      await redirectAfterLogin();
    }
  });

  const resendMfaLoginMutation = useMutation({
    mutationFn: (mfaToken: string) => resendMfaLoginOtp(mfaToken),
    onSuccess: (response) => {
      if (response && 'mfaToken' in response) {
        setMfaLoginState((prev) =>
          prev ? { ...prev, mfaToken: response.mfaToken } : null
        );
      }
    }
  });

  const volunteerOtpMutation = useMutation({
    mutationFn: volunteerOtp,
    onSuccess: () => {
      setStep('otp');
    }
  });

  const signUpMutation = useMutation({
    mutationFn: signUp,
    onSuccess: () => {
      router.push('/volunteer');
    }
  });

  const phoneForm = useForm({
    resolver: zodResolver(logInOTPBodySchema),
    defaultValues: {
      phone: ''
    }
  });

  const otpForm = useForm({
    resolver: zodResolver(otpSchema),
    defaultValues: {
      otp: ''
    }
  });

  const signInForm = useForm<SignInRequestValues>({
    resolver: zodResolver(signInRequestSchema),
    defaultValues: {
      phoneOrEmail: '',
      password: ''
    }
  });

  const onSignInSubmit = (data: SignInRequestValues) => {
    const isEmail = emailSchema.safeParse(data.phoneOrEmail).success;
    const cleanedPhoneOrEmail = isEmail
      ? data.phoneOrEmail.toLowerCase()
      : cleanPhoneNumber(data.phoneOrEmail);
    signInMutation.mutate({
      ...data,
      phoneOrEmail: cleanedPhoneOrEmail
    });
  };

  const params = useSearchParams();

  const volunteerOtpForm = useForm<SignUpVerifyRequestValues>({
    resolver: zodResolver(signUpRequestSchema),
    defaultValues: {
      phone: params.get('phone') || '',
      password: '',
      profile: {
        email: params.get('email') || '',
        firstName: params.get('firstname') || '',
        lastName: params.get('lastname') || ''
      }
    }
  });

  const onVolunteerOtpSubmit = async (data: SignUpVerifyRequestValues) => {
    setPhoneNumber(data.phone);
    const dataWithCleanPhone = {
      ...data,
      phone: cleanPhoneNumber(data.phone)
    };
    return volunteerOtpMutation.mutateAsync(dataWithCleanPhone);
  };

  const onPhoneSubmit = (data: PhoneFormValues) => {
    setPhoneNumber(data.phone);
    const dataWithCleanPhone = {
      phone: cleanPhoneNumber(data.phone)
    };
    sendOtpMutation.mutate(dataWithCleanPhone);
  };

  const onOtpSubmit = (data: OtpFormValues) => {
    queryClient.clear();
    verifyOtpMutation.mutate({
      phone: cleanPhoneNumber(phoneNumber),
      otp: data.otp
    });
  };

  const handleResendOtp = () => {
    sendOtpMutation.mutate({ phone: cleanPhoneNumber(phoneNumber) });
  };

  const handleMfaCancel = () => {
    setMfaLoginState(null);
  };

  return {
    sendOtpMutation,
    verifyOtpMutation,
    signInMutation,
    signUpMutation,
    volunteerOtpMutation,
    verifyMfaLoginMutation,
    resendMfaLoginMutation,
    step,
    phoneForm,
    otpForm,
    onPhoneSubmit,
    onOtpSubmit,
    handleResendOtp,
    phoneNumber,
    signInForm,
    onSignInSubmit,
    volunteerOtpForm,
    onVolunteerOtpSubmit,
    mfaLoginState,
    handleMfaCancel
  };
}
